"use client"

import { useRef, useState } from "react"
import { Bot, MessageSquare, Send, Zap, Copy, Check, AlertCircle, HelpCircle } from "lucide-react"
import Config from "../../Config"
import BottomSheet from "../../components/BottomSheet"

// Streaming reply — the API returns the whole string, we reveal it char-by-char
// so the response feels like a chat model producing it live. Tunable:
const STREAM_STEP_MS = 16 // ~60fps
const STREAM_CHUNK = 2    // chars per tick — 2 feels natural, less "typewriter"

export default function ManualReplyGenerator() {
  const [tweet, setTweet] = useState("")
  const [response, setResponse] = useState("")
  const [loading, setLoading] = useState(false)
  const [responseType, setResponseType] = useState("factual")
  const [copied, setCopied] = useState(false)
  const [sources, setSources] = useState([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [responseId, setResponseId] = useState(0)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [messageKind, setMessageKind] = useState("reply") // "reply" | "notice"
  const streamRef = useRef({ aborted: false })

  const streamText = (fullText) => {
    // cancel any prior stream
    streamRef.current.aborted = true
    const control = { aborted: false }
    streamRef.current = control

    setResponseId((n) => n + 1)
    setResponse("")
    setMessageKind("reply")
    setIsStreaming(true)

    let i = 0
    const step = () => {
      if (control.aborted) return
      i = Math.min(i + STREAM_CHUNK, fullText.length)
      setResponse(fullText.slice(0, i))
      if (i < fullText.length) {
        setTimeout(step, STREAM_STEP_MS)
      } else {
        setIsStreaming(false)
      }
    }
    step()
  }

  // Instant (system) messages — errors, skips, hints — don't stream
  const setInstant = (text) => {
    streamRef.current.aborted = true
    setResponseId((n) => n + 1)
    setIsStreaming(false)
    setMessageKind("notice")
    setResponse(text)
  }

  const handleGenerate = async (overrideText) => {
    // Accept an optional override so a caller can pass text directly instead
    // of relying on tweet state. Defensive check: if this ever gets bound as
    // an onClick without an arrow wrapper, overrideText will be the click
    // event — fall back to state in that case.
    const text = (typeof overrideText === "string" ? overrideText : tweet).trim()
    if (!text) {
      setInstant("Paste a post above, then I'll write you a reply.")
      return
    }

    setLoading(true)
    // clear any prior stream/state
    streamRef.current.aborted = true
    setIsStreaming(false)
    setResponse("")
    setCopied(false)
    setSources([])

    try {
      const bodyPayload = { tweet: text }
      if (responseType === "cynical") {
        bodyPayload.cynicalMode = true
      }

      const res = await fetch(`${Config.API_BASE_URL}${Config.RESONSE_GEN_ROUTE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload),
      })

      if (res.status === 429) {
        setInstant("You've hit the rate limit. Try again in about 30 minutes.")
        return
      }

      const data = await res.json()

      if (!data.is_anti_israel) {
        setInstant("This one doesn't read as anti-Israel, so we skipped it.")
      } else {
        if (Array.isArray(data.sources)) {
          setSources(
            data.sources.map((s) => (typeof s === "string" ? s : s?.url)).filter(Boolean)
          )
        }
        streamText(data.response || "No response generated.")
      }
    } catch {
      setInstant("Something went wrong - try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(response)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const isBusy = loading || isStreaming
  const isNotice = messageKind === "notice"

  return (
    <div id="get-started" className="relative">
      <div
        data-ai-surface="generator"
        data-ai-thinking={isBusy ? "true" : undefined}
        className="relative bg-white"
      >
        {/* Ink variant: painterly blurred blobs (hidden in other variants via CSS) */}
        <div className="ink-blob ink-blob-1" aria-hidden="true" />
        <div className="ink-blob ink-blob-2" aria-hidden="true" />
        <div className="ink-blob ink-blob-3" aria-hidden="true" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 relative">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-[#004aad]/10 text-[#004aad] text-sm font-medium mb-4">
              <Zap className="w-4 h-4" />
              <span>For anyone</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004aad] mb-4 tracking-[-0.01em]">
              Answer any anti-Israel post - with facts.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              See a false claim? Paste it in. Get a sourced, fact-based reply in seconds - and join the
              conversation with confidence.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div
              data-ai-surface="card"
              className="bg-white rounded-2xl border border-[#004aad]/20 shadow-[0_20px_50px_-20px_rgba(0,74,173,0.28)] overflow-hidden"
            >
                <div className="bg-gradient-to-r from-[#004aad] to-[#007bff] text-white py-4 px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      <h3 className="font-medium">Draft a reply</h3>
                    </div>
                    <div className="text-xs bg-white/20 px-2 py-1 rounded">Live</div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Text Input */}
                  <div className="mb-6">
                    <label htmlFor="tweet" className="block text-sm font-medium text-gray-700 mb-2">
                      The post
                    </label>
                    <textarea
                      id="tweet"
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] outline-none transition-all"
                      placeholder="Paste the post you saw..."
                      value={tweet}
                      onChange={(e) => setTweet(e.target.value)}
                    />
                  </div>

                  {/* Demo Buttons */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Or try one of these for example
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        "Israel is an apartheid state.",
                        "Zionism is terrorism.",
                        "The IDF is killing innocent women and children in Gaza.",
                      ].map((demo, i) => (
                        <button
                          key={i}
                          onClick={() => setTweet(demo)}
                          className="press px-3.5 py-1.5 rounded-full text-sm bg-[#004aad]/[0.06] text-[#004aad] border border-[#004aad]/30 hover:bg-[#004aad] hover:text-white transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
                        >
                          {demo.length > 40 ? `${demo.slice(0, 37)}...` : demo}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Response Type */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reply style</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: "factual", label: "Factual" },
                        { value: "cynical", label: "Sharp" },
                      ].map(({ value, label }) => (
                        <div key={value} className="relative">
                          <input
                            type="radio"
                            id={`type-${value}`}
                            name="responseType"
                            className="peer hidden"
                            checked={responseType === value}
                            onChange={() => setResponseType(value)}
                          />
                          <label
                            htmlFor={`type-${value}`}
                            className="press block cursor-pointer border border-gray-300 rounded-lg p-3 text-center text-gray-600 peer-checked:border-[#004aad] peer-checked:bg-[#004aad]/5 peer-checked:text-[#004aad] hover:bg-gray-50 transition-[background-color,border-color,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
                          >
                            <span className="font-medium">{label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Generate Button */}
                  <div className="flex justify-center">
                    <button
                      data-ai-surface="cta"
                      onClick={() => handleGenerate()}
                      disabled={isBusy}
                      className={`press cta-alive relative flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-[background-color,box-shadow] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] bg-[#004aad] text-white ${
                        isBusy
                          ? "cursor-wait"
                          : "hover:bg-[#003c8a] hover:shadow-lg"
                      }`}
                    >
                      <span className="z-10 inline-block min-w-[7rem] text-center">
                        {loading ? "Thinking..." : isStreaming ? "Writing..." : "Generate a Reply"}
                      </span>
                      <Send className={`w-4 h-4 z-10 ${isBusy ? "animate-pulse" : ""}`} />
                    </button>
                  </div>

                  {/* Response Output — sits below the button so the button never jitters */}
                  {response && (
                    <div
                      key={responseId}
                      data-ai-surface="response"
                      className="animate-enter bg-gray-50 ring-1 ring-gray-200 p-4 rounded-xl mt-6 relative"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`rounded-full p-2 text-white ${
                            isNotice ? "bg-amber-500" : "bg-[#004aad]"
                          }`}
                        >
                          {isNotice ? (
                            <AlertCircle className="w-5 h-5" />
                          ) : (
                            <MessageSquare className="w-5 h-5" />
                          )}
                        </div>
                        <div className="pr-10">
                          <div className={`font-medium mb-1 ${isNotice ? "text-amber-700" : "text-[#004aad]"}`}>
                            {isNotice ? "Note" : "The reply"}
                          </div>
                          <p
                            className={`text-gray-600 ${
                              isStreaming ? "stream-cursor" : ""
                            }`}
                          >
                            {response}
                          </p>
                          {!isNotice && sources.length > 0 && !isStreaming && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs font-semibold text-gray-500 mb-1">Sources</p>
                              <ul className="space-y-1">
                                {sources.map((src, i) => (
                                  <li key={i}>
                                    <a
                                      href={src}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-[#004aad] hover:underline break-all"
                                    >
                                      {src}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {!isNotice && !isStreaming && (
                        <button
                          onClick={handleCopy}
                          className="press absolute top-4 right-4 p-2 text-[#004aad] hover:bg-[#004aad]/10 rounded-full transition-colors duration-200"
                          aria-label="Copy to clipboard"
                        >
                          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                      )}
                    </div>
                  )}
                </div>
            </div>

            {/* How it works trigger — sibling of the tool card, inside max-w-3xl */}
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setSheetOpen(true)}
                className="press inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004aad] transition-colors duration-200"
              >
                <HelpCircle className="w-4 h-4" />
                How does it work?
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Behind-the-reply bottom sheet */}
      <BottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Behind the reply"
      >
        <div className="space-y-4 text-slate-600 text-[15px] leading-relaxed">
          <p>
            You paste a post. We classify it, then - if it's hostile - ground the reply in a
            vetted knowledge base rather than guesswork.
          </p>
          <p>Only anti-Israel posts get a reply - we skip everything else.</p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-2">
            <p className="font-semibold mb-3 text-[#004aad] flex items-center gap-2">
              <Bot className="w-4 h-4" />
              Two ways it can reply
            </p>
            <ul className="space-y-2.5 text-sm">
              <li className="flex gap-2">
                <span className="text-[#004aad] font-bold">·</span>
                <span>
                  <span className="font-semibold text-[#004aad]">Factual:</span> Statistics, history and verifiable facts
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#004aad] font-bold">·</span>
                <span>
                  <span className="font-semibold text-[#004aad]">Sharp:</span> Irony and punch to highlight contradictions
                </span>
              </li>
            </ul>
          </div>
        </div>
      </BottomSheet>
    </div>
  )
}
